import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import PhotoUploader from '@/components/PhotoUploader';
import { 
  Stethoscope, LogOut, Plus, Search, Cpu, Calendar, Pill, Video, 
  ClipboardList, BarChart3, Check, X, AlertCircle, FileText, User,
  Syringe, Heart, Activity, Thermometer, Scale, ImagePlus, Eye,
  Phone, Mail, Building2, Clock, ChevronDown, ChevronUp, Loader2
} from 'lucide-react';

const VetDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [prescriptionRequests, setPrescriptionRequests] = useState([]);
  const [consultations, setConsultations] = useState([]);
  
  // Dialog states
  const [microchipSearchOpen, setMicrochipSearchOpen] = useState(false);
  const [implantDialogOpen, setImplantDialogOpen] = useState(false);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [prescriptionDialogOpen, setPrescriptionDialogOpen] = useState(false);
  const [consultationDialogOpen, setConsultationDialogOpen] = useState(false);
  
  // Medical Records states
  const [medicalRecordsDialogOpen, setMedicalRecordsDialogOpen] = useState(false);
  const [addMedicalRecordDialogOpen, setAddMedicalRecordDialogOpen] = useState(false);
  const [addVaccinationDialogOpen, setAddVaccinationDialogOpen] = useState(false);
  const [selectedPetForRecords, setSelectedPetForRecords] = useState(null);
  const [petMedicalData, setPetMedicalData] = useState(null);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [expandedRecords, setExpandedRecords] = useState({});
  
  // Search states
  const [microchipSearchTerm, setMicrochipSearchTerm] = useState('');
  const [microchipSearchResult, setMicrochipSearchResult] = useState(null);
  const [microchipSearching, setMicrochipSearching] = useState(false);
  const [petSearchResults, setPetSearchResults] = useState([]);
  
  // Form states
  const [implantData, setImplantData] = useState({
    microchip_number: '', pet_id: '', owner_id: '', implant_date: '', implant_location: 'Between shoulder blades'
  });
  const [newAppointment, setNewAppointment] = useState({
    owner_name: '', pet_name: '', date: '', time: '', type: 'general', reason: '', notes: ''
  });
  const [newPrescription, setNewPrescription] = useState({
    pet_name: '', owner_name: '', medication: '', dosage: '', frequency: '',
    duration: '', instructions: '', refills_allowed: 0, diagnosis: ''
  });
  const [newConsultation, setNewConsultation] = useState({
    owner_name: '', pet_name: '', scheduled_date: '', scheduled_time: '',
    duration_minutes: 30, fee: 50, reason: ''
  });
  
  // Medical record form
  const [newMedicalRecord, setNewMedicalRecord] = useState({
    record_type: 'exam',
    date: new Date().toISOString().split('T')[0],
    title: '',
    description: '',
    diagnosis: '',
    treatment: '',
    medications: [],
    weight: '',
    temperature: '',
    heart_rate: '',
    respiratory_rate: '',
    findings: '',
    recommendations: '',
    follow_up_date: '',
    attachments: [],
    cost: '',
    paid: false
  });
  
  // Vaccination form
  const [newVaccination, setNewVaccination] = useState({
    vaccine_name: '',
    vaccine_type: 'core',
    manufacturer: '',
    lot_number: '',
    date_given: new Date().toISOString().split('T')[0],
    next_due_date: '',
    dose_number: 1,
    route: 'subcutaneous',
    site: '',
    reaction: '',
    notes: ''
  });
  
  const [commonVaccines, setCommonVaccines] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, appointmentsRes, prescriptionsRes, requestsRes, consultationsRes] = await Promise.all([
        axios.get('/vet/dashboard').catch(() => ({ data: null })),
        axios.get('/vet/appointments').catch(() => ({ data: [] })),
        axios.get('/vet/prescriptions').catch(() => ({ data: [] })),
        axios.get('/vet/prescription-requests').catch(() => ({ data: [] })),
        axios.get('/vet/consultations').catch(() => ({ data: [] }))
      ]);
      
      setDashboardStats(statsRes.data);
      setAppointments(appointmentsRes.data || []);
      setPrescriptions(prescriptionsRes.data || []);
      setPrescriptionRequests(requestsRes.data || []);
      setConsultations(consultationsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Search for pet by microchip
  const handleMicrochipSearch = async () => {
    if (!microchipSearchTerm) return;
    setMicrochipSearching(true);
    setMicrochipSearchResult(null);
    
    try {
      const response = await axios.get(`/vet/microchip-lookup/${microchipSearchTerm}`);
      setMicrochipSearchResult(response.data);
      
      if (response.data.found) {
        toast.success('Pet found!');
      } else {
        toast.info('No pet found with this microchip');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setMicrochipSearching(false);
    }
  };

  // Search pets for medical records
  const handlePetSearch = async (query) => {
    if (!query || query.length < 2) {
      setPetSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`/vet/search/pet?q=${encodeURIComponent(query)}`);
      setPetSearchResults(response.data || []);
    } catch (error) {
      console.error('Pet search failed:', error);
    }
  };

  // Fetch medical records for a pet
  const fetchMedicalRecords = async (identifier) => {
    setLoadingRecords(true);
    try {
      const response = await axios.get(`/vet/pet/${identifier}/medical-records`);
      setPetMedicalData(response.data);
      setMedicalRecordsDialogOpen(true);
      
      // Fetch common vaccines for this species
      const species = response.data.pet?.species || response.data.animal?.species;
      if (species) {
        const vaccinesRes = await axios.get(`/vet/common-vaccines/${species}`);
        setCommonVaccines(vaccinesRes.data || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch medical records');
    } finally {
      setLoadingRecords(false);
    }
  };

  // Open medical records from search result
  const openMedicalRecordsForPet = (searchResult) => {
    const identifier = searchResult.microchip?.microchip_number || 
                       searchResult.animal?.animal_id || 
                       searchResult.pet?.pet_id;
    
    setSelectedPetForRecords({
      name: searchResult.pet?.name || searchResult.animal?.name,
      species: searchResult.pet?.species || searchResult.animal?.species,
      identifier: identifier,
      owner: searchResult.owner,
      shelter: searchResult.shelter
    });
    
    fetchMedicalRecords(identifier);
  };

  // Add medical record
  const handleAddMedicalRecord = async () => {
    if (!selectedPetForRecords?.identifier) return;
    
    try {
      await axios.post(`/vet/pet/${selectedPetForRecords.identifier}/medical-records`, newMedicalRecord);
      toast.success('Medical record added!');
      setAddMedicalRecordDialogOpen(false);
      fetchMedicalRecords(selectedPetForRecords.identifier);
      
      // Reset form
      setNewMedicalRecord({
        record_type: 'exam',
        date: new Date().toISOString().split('T')[0],
        title: '',
        description: '',
        diagnosis: '',
        treatment: '',
        medications: [],
        weight: '',
        temperature: '',
        heart_rate: '',
        respiratory_rate: '',
        findings: '',
        recommendations: '',
        follow_up_date: '',
        attachments: [],
        cost: '',
        paid: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add record');
    }
  };

  // Add vaccination
  const handleAddVaccination = async () => {
    if (!selectedPetForRecords?.identifier) return;
    
    try {
      await axios.post(`/vet/pet/${selectedPetForRecords.identifier}/vaccinations`, newVaccination);
      toast.success('Vaccination recorded!');
      setAddVaccinationDialogOpen(false);
      fetchMedicalRecords(selectedPetForRecords.identifier);
      
      // Reset form
      setNewVaccination({
        vaccine_name: '',
        vaccine_type: 'core',
        manufacturer: '',
        lot_number: '',
        date_given: new Date().toISOString().split('T')[0],
        next_due_date: '',
        dose_number: 1,
        route: 'subcutaneous',
        site: '',
        reaction: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vaccination');
    }
  };

  // Add photos to record
  const handleAddPhotosToRecord = async (recordId, photos) => {
    try {
      await axios.post(`/vet/medical-records/${recordId}/attachments`, {
        attachments: photos.map(p => ({ url: p.url || p, type: 'image' }))
      });
      toast.success('Photos added to record');
      fetchMedicalRecords(selectedPetForRecords.identifier);
    } catch (error) {
      toast.error('Failed to add photos');
    }
  };

  const toggleRecordExpanded = (recordId) => {
    setExpandedRecords(prev => ({
      ...prev,
      [recordId]: !prev[recordId]
    }));
  };

  const getRecordTypeIcon = (type) => {
    switch (type) {
      case 'exam': return <Stethoscope className="w-4 h-4" />;
      case 'surgery': return <Activity className="w-4 h-4" />;
      case 'xray': return <FileText className="w-4 h-4" />;
      case 'lab': return <ClipboardList className="w-4 h-4" />;
      case 'spay_neuter': return <Heart className="w-4 h-4" />;
      case 'dental': return <Stethoscope className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getRecordTypeBadge = (type) => {
    const colors = {
      exam: 'bg-blue-100 text-blue-800',
      surgery: 'bg-red-100 text-red-800',
      xray: 'bg-purple-100 text-purple-800',
      lab: 'bg-yellow-100 text-yellow-800',
      spay_neuter: 'bg-pink-100 text-pink-800',
      dental: 'bg-green-100 text-green-800',
      treatment: 'bg-orange-100 text-orange-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors.other;
  };

  // Handlers for other forms
  const handleImplantMicrochip = async () => {
    try {
      await axios.post('/vet/implant-microchip', implantData);
      toast.success('Microchip implanted successfully');
      setImplantDialogOpen(false);
      setImplantData({
        microchip_number: '', pet_id: '', owner_id: '', implant_date: '', implant_location: 'Between shoulder blades'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record implant');
    }
  };

  const handleCreateAppointment = async () => {
    try {
      await axios.post('/vet/appointments', newAppointment);
      toast.success('Appointment scheduled');
      setAppointmentDialogOpen(false);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to create appointment');
    }
  };

  const handleCreatePrescription = async () => {
    try {
      await axios.post('/vet/prescriptions', newPrescription);
      toast.success('Prescription created');
      setPrescriptionDialogOpen(false);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to create prescription');
    }
  };

  const handleUpdateAppointmentStatus = async (appointmentId, status) => {
    try {
      await axios.put(`/vet/appointments/${appointmentId}/status?status=${status}`);
      toast.success('Appointment updated');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Stethoscope className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {user.organization_name || 'Veterinary Clinic'}
              </h1>
              <p className="text-sm text-stone-500">Welcome, Dr. {user.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setMicrochipSearchOpen(true)}>
              <Search className="w-4 h-4 mr-2" />Pet Lookup
            </Button>
            <Button variant="ghost" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">
              <BarChart3 className="w-4 h-4 mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="records">
              <ClipboardList className="w-4 h-4 mr-2" />Medical Records
            </TabsTrigger>
            <TabsTrigger value="appointments">
              <Calendar className="w-4 h-4 mr-2" />Appointments
            </TabsTrigger>
            <TabsTrigger value="prescriptions">
              <Pill className="w-4 h-4 mr-2" />Prescriptions
            </TabsTrigger>
            <TabsTrigger value="consultations">
              <Video className="w-4 h-4 mr-2" />Consultations
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Today's Appointments</p>
                      <p className="text-3xl font-bold">{dashboardStats?.todays_appointments || 0}</p>
                    </div>
                    <Calendar className="w-10 h-10 text-primary opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Active Prescriptions</p>
                      <p className="text-3xl font-bold">{prescriptions.length}</p>
                    </div>
                    <Pill className="w-10 h-10 text-green-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Pending Requests</p>
                      <p className="text-3xl font-bold">{prescriptionRequests.length}</p>
                    </div>
                    <AlertCircle className="w-10 h-10 text-yellow-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Consultations</p>
                      <p className="text-3xl font-bold">{consultations.length}</p>
                    </div>
                    <Video className="w-10 h-10 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => setMicrochipSearchOpen(true)}>
                    <Search className="w-4 h-4 mr-2" />Search Pet by Microchip
                  </Button>
                  <Button variant="outline" onClick={() => setImplantDialogOpen(true)}>
                    <Cpu className="w-4 h-4 mr-2" />Implant Microchip
                  </Button>
                  <Button variant="outline" onClick={() => setAppointmentDialogOpen(true)}>
                    <Calendar className="w-4 h-4 mr-2" />New Appointment
                  </Button>
                  <Button variant="outline" onClick={() => setPrescriptionDialogOpen(true)}>
                    <Pill className="w-4 h-4 mr-2" />Write Prescription
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Upcoming Appointments</CardTitle>
                </CardHeader>
                <CardContent>
                  {appointments.filter(a => a.status === 'scheduled').slice(0, 5).map((apt) => (
                    <div key={apt.appointment_id} className="flex justify-between items-center py-3 border-b last:border-0">
                      <div>
                        <p className="font-medium">{apt.pet_name || 'Pet'}</p>
                        <p className="text-sm text-stone-500">{apt.date} at {apt.time}</p>
                      </div>
                      <Badge>{apt.type}</Badge>
                    </div>
                  ))}
                  {appointments.filter(a => a.status === 'scheduled').length === 0 && (
                    <p className="text-stone-500 text-center py-4">No upcoming appointments</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Prescription Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  {prescriptionRequests.slice(0, 5).map((req) => (
                    <div key={req.request_id} className="flex justify-between items-center py-3 border-b last:border-0">
                      <div>
                        <p className="font-medium">{req.pet_name}</p>
                        <p className="text-sm text-stone-500">{req.medication}</p>
                      </div>
                      <Badge variant="secondary">{req.status}</Badge>
                    </div>
                  ))}
                  {prescriptionRequests.length === 0 && (
                    <p className="text-stone-500 text-center py-4">No pending requests</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Medical Records Tab */}
          <TabsContent value="records">
            <Card>
              <CardHeader>
                <CardTitle>Pet Medical Records</CardTitle>
                <CardDescription>Search any pet by microchip number to view or add medical records</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-xl mx-auto space-y-4">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        placeholder="Enter microchip number or pet name..."
                        value={microchipSearchTerm}
                        onChange={(e) => {
                          setMicrochipSearchTerm(e.target.value);
                          handlePetSearch(e.target.value);
                        }}
                        className="pl-10"
                        onKeyPress={(e) => e.key === 'Enter' && handleMicrochipSearch()}
                      />
                    </div>
                    <Button onClick={handleMicrochipSearch} disabled={microchipSearching}>
                      {microchipSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                    </Button>
                  </div>

                  {/* Search Results */}
                  {petSearchResults.length > 0 && (
                    <div className="border rounded-lg divide-y">
                      {petSearchResults.map((result, idx) => (
                        <div 
                          key={idx} 
                          className="p-4 hover:bg-stone-50 cursor-pointer flex justify-between items-center"
                          onClick={() => openMedicalRecordsForPet(result)}
                        >
                          <div>
                            <p className="font-medium">{result.pet?.name || result.animal?.name}</p>
                            <p className="text-sm text-stone-500">
                              {result.pet?.species || result.animal?.species} • 
                              {result.microchip?.microchip_number || result.animal?.microchip_number || 'No microchip'}
                            </p>
                            <p className="text-xs text-stone-400">
                              Owner: {result.owner?.name || result.shelter?.organization_name || 'Unknown'}
                            </p>
                          </div>
                          <Button size="sm" variant="outline">
                            <Eye className="w-4 h-4 mr-1" />View Records
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Direct search result */}
                  {microchipSearchResult && microchipSearchResult.found && petSearchResults.length === 0 && (
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-lg">
                              {microchipSearchResult.pet?.name || microchipSearchResult.animal?.name || 'Pet Found'}
                            </h3>
                            <p className="text-stone-600">
                              {microchipSearchResult.pet?.species || microchipSearchResult.animal?.species} • 
                              {microchipSearchResult.pet?.breed || microchipSearchResult.animal?.breed}
                            </p>
                            <p className="text-sm text-stone-500 mt-1">
                              Microchip: {microchipSearchResult.microchip?.microchip_number || microchipSearchTerm}
                            </p>
                            {(microchipSearchResult.owner || microchipSearchResult.shelter) && (
                              <div className="mt-2 text-sm">
                                <p className="font-medium">
                                  {microchipSearchResult.owner?.name || microchipSearchResult.shelter?.organization_name}
                                </p>
                                <p className="text-stone-500">
                                  {microchipSearchResult.owner?.email || microchipSearchResult.shelter?.email}
                                </p>
                              </div>
                            )}
                          </div>
                          <Button 
                            onClick={() => {
                              setSelectedPetForRecords({
                                name: microchipSearchResult.pet?.name || microchipSearchResult.animal?.name,
                                species: microchipSearchResult.pet?.species || microchipSearchResult.animal?.species,
                                identifier: microchipSearchResult.microchip?.microchip_number || 
                                           microchipSearchResult.animal?.animal_id ||
                                           microchipSearchResult.pet?.pet_id,
                                owner: microchipSearchResult.owner,
                                shelter: microchipSearchResult.shelter
                              });
                              fetchMedicalRecords(
                                microchipSearchResult.microchip?.microchip_number || 
                                microchipSearchResult.animal?.animal_id ||
                                microchipSearchResult.pet?.pet_id
                              );
                            }}
                          >
                            <ClipboardList className="w-4 h-4 mr-2" />View Medical Records
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {microchipSearchResult && !microchipSearchResult.found && (
                    <Card className="bg-yellow-50 border-yellow-200">
                      <CardContent className="pt-6 text-center">
                        <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-2" />
                        <p className="font-medium">No pet found with this microchip number</p>
                        <p className="text-sm text-stone-500">Please verify the microchip number and try again</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Appointments</CardTitle>
                  <CardDescription>Manage patient appointments</CardDescription>
                </div>
                <Button onClick={() => setAppointmentDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />New Appointment
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {appointments.map((apt) => (
                    <div key={apt.appointment_id} className="p-4 border rounded-lg flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{apt.pet_name || 'Pet'}</h4>
                          <Badge variant={apt.status === 'completed' ? 'default' : apt.status === 'cancelled' ? 'destructive' : 'secondary'}>
                            {apt.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-stone-500">{apt.owner_name}</p>
                        <p className="text-sm">{apt.date} at {apt.time} • {apt.type}</p>
                        {apt.reason && <p className="text-sm text-stone-600 mt-1">{apt.reason}</p>}
                      </div>
                      {apt.status === 'scheduled' && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleUpdateAppointmentStatus(apt.appointment_id, 'completed')}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleUpdateAppointmentStatus(apt.appointment_id, 'cancelled')}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  {appointments.length === 0 && (
                    <p className="text-center text-stone-500 py-8">No appointments scheduled</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prescriptions Tab */}
          <TabsContent value="prescriptions">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Prescriptions</CardTitle>
                  <CardDescription>Manage and create prescriptions</CardDescription>
                </div>
                <Button onClick={() => setPrescriptionDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />New Prescription
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {prescriptions.map((rx) => (
                    <div key={rx.prescription_id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{rx.medication}</h4>
                          <p className="text-sm text-stone-500">For: {rx.pet_name} ({rx.owner_name})</p>
                          <p className="text-sm">{rx.dosage} • {rx.frequency} • {rx.duration}</p>
                          {rx.instructions && <p className="text-sm text-stone-600 mt-1">{rx.instructions}</p>}
                        </div>
                        <Badge variant={rx.status === 'active' ? 'default' : 'secondary'}>{rx.status}</Badge>
                      </div>
                    </div>
                  ))}
                  {prescriptions.length === 0 && (
                    <p className="text-center text-stone-500 py-8">No prescriptions created</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Consultations Tab */}
          <TabsContent value="consultations">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Video Consultations</CardTitle>
                  <CardDescription>Manage virtual appointments</CardDescription>
                </div>
                <Button onClick={() => setConsultationDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />Schedule Consultation
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {consultations.map((consult) => (
                    <div key={consult.consultation_id} className="p-4 border rounded-lg flex justify-between items-center">
                      <div>
                        <h4 className="font-medium">{consult.pet_name} - {consult.owner_name}</h4>
                        <p className="text-sm">{consult.scheduled_date} at {consult.scheduled_time}</p>
                        <p className="text-sm text-stone-500">{consult.reason}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge>${consult.fee}</Badge>
                        <Badge variant={consult.status === 'completed' ? 'default' : 'secondary'}>{consult.status}</Badge>
                      </div>
                    </div>
                  ))}
                  {consultations.length === 0 && (
                    <p className="text-center text-stone-500 py-8">No consultations scheduled</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Microchip Search Dialog */}
      <Dialog open={microchipSearchOpen} onOpenChange={setMicrochipSearchOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Search Pet by Microchip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter microchip number..."
                value={microchipSearchTerm}
                onChange={(e) => setMicrochipSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleMicrochipSearch()}
              />
              <Button onClick={handleMicrochipSearch} disabled={microchipSearching}>
                {microchipSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {microchipSearchResult && microchipSearchResult.found && (
              <Card className="bg-green-50">
                <CardContent className="pt-4">
                  <h3 className="font-semibold mb-2">Pet Found!</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Name:</strong> {microchipSearchResult.pet?.name || microchipSearchResult.animal?.name}</p>
                    <p><strong>Species:</strong> {microchipSearchResult.pet?.species || microchipSearchResult.animal?.species}</p>
                    <p><strong>Breed:</strong> {microchipSearchResult.pet?.breed || microchipSearchResult.animal?.breed}</p>
                    {microchipSearchResult.owner && (
                      <>
                        <hr className="my-2" />
                        <p><strong>Owner:</strong> {microchipSearchResult.owner.name}</p>
                        <p><strong>Email:</strong> {microchipSearchResult.owner.email}</p>
                        <p><strong>Phone:</strong> {microchipSearchResult.owner.phone}</p>
                      </>
                    )}
                    {microchipSearchResult.shelter && (
                      <>
                        <hr className="my-2" />
                        <p><strong>Shelter:</strong> {microchipSearchResult.shelter.organization_name}</p>
                        <p><strong>Contact:</strong> {microchipSearchResult.shelter.email}</p>
                      </>
                    )}
                  </div>
                  <Button 
                    className="mt-4 w-full"
                    onClick={() => {
                      setMicrochipSearchOpen(false);
                      setSelectedPetForRecords({
                        name: microchipSearchResult.pet?.name || microchipSearchResult.animal?.name,
                        species: microchipSearchResult.pet?.species || microchipSearchResult.animal?.species,
                        identifier: microchipSearchResult.microchip?.microchip_number || microchipSearchTerm,
                        owner: microchipSearchResult.owner,
                        shelter: microchipSearchResult.shelter
                      });
                      fetchMedicalRecords(microchipSearchResult.microchip?.microchip_number || microchipSearchTerm);
                    }}
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />View/Add Medical Records
                  </Button>
                </CardContent>
              </Card>
            )}

            {microchipSearchResult && !microchipSearchResult.found && (
              <div className="text-center py-4 text-stone-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-2 text-yellow-500" />
                <p>No pet found with this microchip number</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Medical Records View Dialog */}
      <Dialog open={medicalRecordsDialogOpen} onOpenChange={setMedicalRecordsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Medical Records - {selectedPetForRecords?.name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingRecords ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : petMedicalData && (
            <div className="space-y-6">
              {/* Pet Info Header */}
              <Card className="bg-stone-50">
                <CardContent className="pt-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-stone-500">Pet</p>
                      <p className="font-medium">{petMedicalData.pet?.name || petMedicalData.animal?.name}</p>
                      <p className="text-sm">{petMedicalData.pet?.species || petMedicalData.animal?.species} - {petMedicalData.pet?.breed || petMedicalData.animal?.breed}</p>
                    </div>
                    <div>
                      <p className="text-sm text-stone-500">Microchip</p>
                      <p className="font-medium font-mono">{petMedicalData.microchip?.microchip_number || 'Not registered'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-stone-500">Owner/Organization</p>
                      <p className="font-medium">{petMedicalData.owner?.name || petMedicalData.owner?.organization_name || 'Unknown'}</p>
                      {petMedicalData.owner?.phone && (
                        <p className="text-sm flex items-center gap-1"><Phone className="w-3 h-3" />{petMedicalData.owner.phone}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button onClick={() => setAddMedicalRecordDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />Add Medical Record
                </Button>
                <Button variant="outline" onClick={() => setAddVaccinationDialogOpen(true)}>
                  <Syringe className="w-4 h-4 mr-2" />Add Vaccination
                </Button>
              </div>

              {/* Vaccinations Section */}
              {petMedicalData.vaccinations?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Syringe className="w-4 h-4" />Vaccination Records
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {petMedicalData.vaccinations.map((vax) => (
                      <Card key={vax.vaccination_id} className="bg-green-50 border-green-200">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{vax.vaccine_name}</p>
                              <p className="text-sm text-stone-600">Given: {new Date(vax.date_given).toLocaleDateString()}</p>
                              {vax.next_due_date && (
                                <p className="text-sm text-orange-600">Next due: {new Date(vax.next_due_date).toLocaleDateString()}</p>
                              )}
                              <p className="text-xs text-stone-500 mt-1">
                                By: {vax.entered_by?.name} ({vax.clinic_name})
                              </p>
                            </div>
                            <Badge variant="outline">{vax.vaccine_type}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Medical Records Section */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />Medical History ({petMedicalData.medical_records?.length || 0} records)
                </h3>
                <div className="space-y-3">
                  {petMedicalData.medical_records?.map((record) => (
                    <Card key={record.record_id}>
                      <CardContent className="pt-4">
                        <div 
                          className="flex justify-between items-start cursor-pointer"
                          onClick={() => toggleRecordExpanded(record.record_id)}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded ${getRecordTypeBadge(record.record_type)}`}>
                              {getRecordTypeIcon(record.record_type)}
                            </div>
                            <div>
                              <p className="font-medium">{record.title || record.record_type}</p>
                              <p className="text-sm text-stone-500">{new Date(record.date).toLocaleDateString()}</p>
                              <p className="text-xs text-stone-400">
                                By: {record.entered_by?.name} - {record.clinic_name}
                                {record.entered_by?.phone && ` (${record.entered_by.phone})`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getRecordTypeBadge(record.record_type)}>{record.record_type}</Badge>
                            {expandedRecords[record.record_id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </div>
                        </div>

                        {expandedRecords[record.record_id] && (
                          <div className="mt-4 pt-4 border-t space-y-3">
                            {record.description && (
                              <div>
                                <p className="text-sm font-medium text-stone-500">Description</p>
                                <p className="text-sm">{record.description}</p>
                              </div>
                            )}
                            {record.diagnosis && (
                              <div>
                                <p className="text-sm font-medium text-stone-500">Diagnosis</p>
                                <p className="text-sm">{record.diagnosis}</p>
                              </div>
                            )}
                            {record.treatment && (
                              <div>
                                <p className="text-sm font-medium text-stone-500">Treatment</p>
                                <p className="text-sm">{record.treatment}</p>
                              </div>
                            )}
                            {(record.weight || record.temperature) && (
                              <div className="flex gap-4">
                                {record.weight && <p className="text-sm"><Scale className="w-3 h-3 inline mr-1" />{record.weight} lbs</p>}
                                {record.temperature && <p className="text-sm"><Thermometer className="w-3 h-3 inline mr-1" />{record.temperature}°F</p>}
                                {record.heart_rate && <p className="text-sm"><Heart className="w-3 h-3 inline mr-1" />{record.heart_rate} bpm</p>}
                              </div>
                            )}
                            {record.recommendations && (
                              <div>
                                <p className="text-sm font-medium text-stone-500">Recommendations</p>
                                <p className="text-sm">{record.recommendations}</p>
                              </div>
                            )}
                            {record.attachments?.length > 0 && (
                              <div>
                                <p className="text-sm font-medium text-stone-500 mb-2">Attachments</p>
                                <div className="flex gap-2 flex-wrap">
                                  {record.attachments.map((att, idx) => (
                                    <a key={idx} href={att.url || att} target="_blank" rel="noopener noreferrer">
                                      <img src={att.url || att} alt={`Attachment ${idx + 1}`} className="w-20 h-20 object-cover rounded border" />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="flex justify-between items-center pt-2">
                              <p className="text-xs text-stone-400">
                                Contact: {record.entered_by?.email} {record.entered_by?.phone && `| ${record.entered_by.phone}`}
                              </p>
                              {record.cost && (
                                <Badge variant="outline">${record.cost} {record.paid ? '(Paid)' : '(Unpaid)'}</Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {(!petMedicalData.medical_records || petMedicalData.medical_records.length === 0) && (
                    <p className="text-center text-stone-500 py-4">No medical records found</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Medical Record Dialog */}
      <Dialog open={addMedicalRecordDialogOpen} onOpenChange={setAddMedicalRecordDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Medical Record - {selectedPetForRecords?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Record Type *</Label>
              <Select value={newMedicalRecord.record_type} onValueChange={(v) => setNewMedicalRecord({...newMedicalRecord, record_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="exam">General Exam</SelectItem>
                  <SelectItem value="surgery">Surgery</SelectItem>
                  <SelectItem value="xray">X-Ray / Imaging</SelectItem>
                  <SelectItem value="lab">Lab Work</SelectItem>
                  <SelectItem value="dental">Dental</SelectItem>
                  <SelectItem value="spay_neuter">Spay/Neuter</SelectItem>
                  <SelectItem value="treatment">Treatment</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date *</Label>
              <Input type="date" value={newMedicalRecord.date} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, date: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Title *</Label>
              <Input value={newMedicalRecord.title} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, title: e.target.value})} placeholder="e.g., Annual Wellness Exam" />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea value={newMedicalRecord.description} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, description: e.target.value})} placeholder="Detailed description of the visit/procedure" />
            </div>
            <div>
              <Label>Diagnosis</Label>
              <Input value={newMedicalRecord.diagnosis} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, diagnosis: e.target.value})} />
            </div>
            <div>
              <Label>Treatment</Label>
              <Input value={newMedicalRecord.treatment} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, treatment: e.target.value})} />
            </div>
            <div>
              <Label>Weight (lbs)</Label>
              <Input type="number" value={newMedicalRecord.weight} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, weight: e.target.value})} />
            </div>
            <div>
              <Label>Temperature (°F)</Label>
              <Input type="number" step="0.1" value={newMedicalRecord.temperature} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, temperature: e.target.value})} />
            </div>
            <div>
              <Label>Heart Rate (bpm)</Label>
              <Input type="number" value={newMedicalRecord.heart_rate} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, heart_rate: e.target.value})} />
            </div>
            <div>
              <Label>Follow-up Date</Label>
              <Input type="date" value={newMedicalRecord.follow_up_date} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, follow_up_date: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Findings & Notes</Label>
              <Textarea value={newMedicalRecord.findings} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, findings: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Recommendations</Label>
              <Textarea value={newMedicalRecord.recommendations} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, recommendations: e.target.value})} />
            </div>
            <div>
              <Label>Cost ($)</Label>
              <Input type="number" value={newMedicalRecord.cost} onChange={(e) => setNewMedicalRecord({...newMedicalRecord, cost: e.target.value})} />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Checkbox checked={newMedicalRecord.paid} onCheckedChange={(c) => setNewMedicalRecord({...newMedicalRecord, paid: c})} />
              <Label>Payment Received</Label>
            </div>
            <div className="col-span-2">
              <Label>Attachments (X-rays, Images, Documents)</Label>
              <PhotoUploader
                photos={newMedicalRecord.attachments.map((url, idx) => ({ id: `att_${idx}`, url, type: 'image' }))}
                onPhotosChange={(photos) => setNewMedicalRecord({...newMedicalRecord, attachments: photos.map(p => p.url || p)})}
                maxPhotos={10}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMedicalRecordDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddMedicalRecord} disabled={!newMedicalRecord.title || !newMedicalRecord.record_type}>
              Save Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Vaccination Dialog */}
      <Dialog open={addVaccinationDialogOpen} onOpenChange={setAddVaccinationDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Syringe className="w-5 h-5" />Add Vaccination - {selectedPetForRecords?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vaccine Name *</Label>
              <Select value={newVaccination.vaccine_name} onValueChange={(v) => setNewVaccination({...newVaccination, vaccine_name: v})}>
                <SelectTrigger><SelectValue placeholder="Select or type vaccine name" /></SelectTrigger>
                <SelectContent>
                  {commonVaccines.map((vax) => (
                    <SelectItem key={vax} value={vax}>{vax}</SelectItem>
                  ))}
                  <SelectItem value="other">Other (specify in notes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vaccine Type</Label>
                <Select value={newVaccination.vaccine_type} onValueChange={(v) => setNewVaccination({...newVaccination, vaccine_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="core">Core</SelectItem>
                    <SelectItem value="non-core">Non-Core</SelectItem>
                    <SelectItem value="required">Required by Law</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Route</Label>
                <Select value={newVaccination.route} onValueChange={(v) => setNewVaccination({...newVaccination, route: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="subcutaneous">Subcutaneous</SelectItem>
                    <SelectItem value="intramuscular">Intramuscular</SelectItem>
                    <SelectItem value="intranasal">Intranasal</SelectItem>
                    <SelectItem value="oral">Oral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date Given *</Label>
                <Input type="date" value={newVaccination.date_given} onChange={(e) => setNewVaccination({...newVaccination, date_given: e.target.value})} />
              </div>
              <div>
                <Label>Next Due Date</Label>
                <Input type="date" value={newVaccination.next_due_date} onChange={(e) => setNewVaccination({...newVaccination, next_due_date: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Manufacturer</Label>
                <Input value={newVaccination.manufacturer} onChange={(e) => setNewVaccination({...newVaccination, manufacturer: e.target.value})} />
              </div>
              <div>
                <Label>Lot Number</Label>
                <Input value={newVaccination.lot_number} onChange={(e) => setNewVaccination({...newVaccination, lot_number: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Dose Number</Label>
                <Input type="number" min="1" value={newVaccination.dose_number} onChange={(e) => setNewVaccination({...newVaccination, dose_number: parseInt(e.target.value)})} />
              </div>
              <div>
                <Label>Injection Site</Label>
                <Input value={newVaccination.site} onChange={(e) => setNewVaccination({...newVaccination, site: e.target.value})} placeholder="e.g., Right rear leg" />
              </div>
            </div>
            <div>
              <Label>Reaction (if any)</Label>
              <Input value={newVaccination.reaction} onChange={(e) => setNewVaccination({...newVaccination, reaction: e.target.value})} placeholder="Note any adverse reactions" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={newVaccination.notes} onChange={(e) => setNewVaccination({...newVaccination, notes: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddVaccinationDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddVaccination} disabled={!newVaccination.vaccine_name || !newVaccination.date_given}>
              <Syringe className="w-4 h-4 mr-2" />Record Vaccination
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Implant Microchip Dialog */}
      <Dialog open={implantDialogOpen} onOpenChange={setImplantDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Implant Microchip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Microchip Number *</Label>
              <Input value={implantData.microchip_number} onChange={(e) => setImplantData({...implantData, microchip_number: e.target.value})} />
            </div>
            <div>
              <Label>Implant Date *</Label>
              <Input type="date" value={implantData.implant_date} onChange={(e) => setImplantData({...implantData, implant_date: e.target.value})} />
            </div>
            <div>
              <Label>Implant Location</Label>
              <Input value={implantData.implant_location} onChange={(e) => setImplantData({...implantData, implant_location: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImplantDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleImplantMicrochip}>Record Implant</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appointment Dialog */}
      <Dialog open={appointmentDialogOpen} onOpenChange={setAppointmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pet Name *</Label>
                <Input value={newAppointment.pet_name} onChange={(e) => setNewAppointment({...newAppointment, pet_name: e.target.value})} />
              </div>
              <div>
                <Label>Owner Name *</Label>
                <Input value={newAppointment.owner_name} onChange={(e) => setNewAppointment({...newAppointment, owner_name: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={newAppointment.date} onChange={(e) => setNewAppointment({...newAppointment, date: e.target.value})} />
              </div>
              <div>
                <Label>Time *</Label>
                <Input type="time" value={newAppointment.time} onChange={(e) => setNewAppointment({...newAppointment, time: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Appointment Type</Label>
              <Select value={newAppointment.type} onValueChange={(v) => setNewAppointment({...newAppointment, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General Checkup</SelectItem>
                  <SelectItem value="vaccination">Vaccination</SelectItem>
                  <SelectItem value="surgery">Surgery</SelectItem>
                  <SelectItem value="dental">Dental</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                  <SelectItem value="follow-up">Follow-up</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea value={newAppointment.reason} onChange={(e) => setNewAppointment({...newAppointment, reason: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppointmentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateAppointment}>Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prescription Dialog */}
      <Dialog open={prescriptionDialogOpen} onOpenChange={setPrescriptionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Write Prescription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pet Name *</Label>
                <Input value={newPrescription.pet_name} onChange={(e) => setNewPrescription({...newPrescription, pet_name: e.target.value})} />
              </div>
              <div>
                <Label>Owner Name *</Label>
                <Input value={newPrescription.owner_name} onChange={(e) => setNewPrescription({...newPrescription, owner_name: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Medication *</Label>
              <Input value={newPrescription.medication} onChange={(e) => setNewPrescription({...newPrescription, medication: e.target.value})} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Dosage *</Label>
                <Input value={newPrescription.dosage} onChange={(e) => setNewPrescription({...newPrescription, dosage: e.target.value})} placeholder="e.g., 10mg" />
              </div>
              <div>
                <Label>Frequency *</Label>
                <Input value={newPrescription.frequency} onChange={(e) => setNewPrescription({...newPrescription, frequency: e.target.value})} placeholder="e.g., twice daily" />
              </div>
              <div>
                <Label>Duration *</Label>
                <Input value={newPrescription.duration} onChange={(e) => setNewPrescription({...newPrescription, duration: e.target.value})} placeholder="e.g., 7 days" />
              </div>
            </div>
            <div>
              <Label>Diagnosis</Label>
              <Input value={newPrescription.diagnosis} onChange={(e) => setNewPrescription({...newPrescription, diagnosis: e.target.value})} />
            </div>
            <div>
              <Label>Instructions</Label>
              <Textarea value={newPrescription.instructions} onChange={(e) => setNewPrescription({...newPrescription, instructions: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrescriptionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePrescription}>Create Prescription</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VetDashboard;
